import { useEffect, useState } from 'preact/hooks'
import type { CloudLocation } from '../../types'
import {
  getUserProfile,
  saveUserProfile,
  listCloudLocations,
  upsertCloudLocation,
  deleteCloudLocation,
  downloadBlobAsUrl,
  type UserProfile,
} from '../../services/api'
import { EmptyState, formatDate, refreshBtnStyle } from './shared'
import { ConfirmDialog, DeleteButton } from './CrudHelpers'
import { FullscreenImageViewer } from '../FullscreenImageViewer'

const locationImageCache = new Map<string, string>()

function locationImageKey(l: CloudLocation, preferFull = false): string {
  const full = (l.imageBlobKey as string | undefined) || (l.image_blob_key as string | undefined)
  const thumb = (l.imageThumbnailKey as string | undefined) || (l.image_thumbnail_key as string | undefined)
  if (preferFull) return full || thumb || ''
  return thumb || full || ''
}

function LocationImage({ location, size, preferFull, onZoom }: { location: CloudLocation; size: number; preferFull?: boolean; onZoom?: (url: string) => void }) {
  const key = locationImageKey(location, preferFull)
  const [url, setUrl] = useState<string | null>(key ? locationImageCache.get(key) || null : null)

  useEffect(() => {
    let cancelled = false
    if (!key) { setUrl(null); return }
    if (locationImageCache.has(key)) {
      setUrl(locationImageCache.get(key) || null)
      return
    }
    downloadBlobAsUrl(key).then((r) => {
      if (cancelled) return
      if (r?.url) {
        locationImageCache.set(key, r.url)
        setUrl(r.url)
      }
    })
    return () => { cancelled = true }
  }, [key])

  const base: Record<string, string | number> = {
    width: size,
    height: size,
    minWidth: size,
    borderRadius: 10,
    overflow: 'hidden',
    background: 'rgba(45,165,160,0.18)',
    border: '1px solid rgba(250,250,250,0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: Math.max(10, Math.round(size * 0.35)),
    color: 'rgba(250,250,250,0.5)',
  }

  if (url) {
    const clickable = !!onZoom
    return (
      <div
        style={{ ...base, cursor: clickable ? 'zoom-in' : undefined }}
        onClick={clickable ? (e) => { e.stopPropagation(); onZoom!(url) } : undefined}
        title={clickable ? 'Click to view fullscreen' : undefined}
      >
        <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </div>
    )
  }
  return <div style={base}>{'\u{1F4CD}'}</div>
}

export function UserView() {
  const [profile, setProfile] = useState<UserProfile>({ user_name: '', additional_details: '' })
  const [draft, setDraft] = useState<UserProfile>({ user_name: '', additional_details: '' })
  const [locations, setLocations] = useState<CloudLocation[]>([])
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null)
  const [locationDraft, setLocationDraft] = useState<CloudLocation | null>(null)
  const [confirmDeleteLocation, setConfirmDeleteLocation] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savingLocation, setSavingLocation] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedFlash, setSavedFlash] = useState(false)
  const [locationSavedFlash, setLocationSavedFlash] = useState(false)
  const [zoomImageUrl, setZoomImageUrl] = useState<string | null>(null)

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const [p, locs] = await Promise.all([getUserProfile(), listCloudLocations()])
      setProfile(p)
      setDraft(p)
      setLocations(locs)
    } catch (e) {
      console.error('Failed to load user profile:', e)
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const selectedLocation = locations.find((l) => l.id === selectedLocationId) || null

  useEffect(() => {
    setLocationDraft(selectedLocation ? { ...selectedLocation } : null)
    setConfirmDeleteLocation(false)
  }, [selectedLocationId])

  const hasChanges = draft.user_name !== profile.user_name || draft.additional_details !== profile.additional_details

  const save = async () => {
    setSaving(true)
    try {
      await saveUserProfile(draft)
      setProfile(draft)
      setSavedFlash(true)
      setTimeout(() => setSavedFlash(false), 1500)
    } catch (e) {
      console.error('Failed to save user profile:', e)
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  const saveLocation = async () => {
    if (!locationDraft || !selectedLocation) return
    setSavingLocation(true)
    try {
      const updated: CloudLocation = {
        ...selectedLocation,
        ...locationDraft,
        updatedAt: new Date().toISOString(),
      }
      await upsertCloudLocation(updated)
      setLocations((prev) => prev.map((l) => (l.id === updated.id ? updated : l)))
      setLocationSavedFlash(true)
      setTimeout(() => setLocationSavedFlash(false), 1500)
    } catch (e) {
      console.error('Failed to save location:', e)
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setSavingLocation(false)
    }
  }

  const deleteLocation = async () => {
    if (!selectedLocation) return
    try {
      await deleteCloudLocation(selectedLocation.id)
      setLocations((prev) => prev.filter((l) => l.id !== selectedLocation.id))
      setSelectedLocationId(null)
      setConfirmDeleteLocation(false)
    } catch (e) {
      console.error('Failed to delete location:', e)
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  const locationHasChanges =
    !!locationDraft && !!selectedLocation &&
    (
      (locationDraft.title || '') !== (selectedLocation.title || '')
      || (locationDraft.address || '') !== (selectedLocation.address || '')
      || (locationDraft.latitude ?? null) !== (selectedLocation.latitude ?? null)
      || (locationDraft.longitude ?? null) !== (selectedLocation.longitude ?? null)
    )

  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      {/* Left column — profile + locations list */}
      <div style={{ flex: 1, overflow: 'auto', padding: 24, maxWidth: 900, minWidth: 320 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ flex: 1, fontSize: 20, fontWeight: 600, margin: 0 }}>User Profile</h2>
          <button onClick={load} style={refreshBtnStyle} disabled={loading}>
            {loading ? '...' : 'Refresh'}
          </button>
        </div>

        {error && (
          <div style={{ marginBottom: 16, padding: 10, background: 'rgba(198,40,40,0.15)', border: '1px solid rgba(198,40,40,0.4)', borderRadius: 8, color: '#ef9a9a', fontSize: 13 }}>
            {error}
          </div>
        )}

        {/* User Information */}
        <div style={sectionCardStyle}>
          <div style={sectionTitleStyle}>User Information</div>

          <div style={fieldStyle}>
            <label style={labelStyle}>User Name</label>
            <input
              type="text"
              value={draft.user_name}
              onInput={(e) => setDraft({ ...draft, user_name: (e.target as HTMLInputElement).value })}
              placeholder="Your name"
              style={textInputStyle}
              disabled={loading}
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>My Business</label>
            <textarea
              value={draft.additional_details}
              onInput={(e) => setDraft({ ...draft, additional_details: (e.target as HTMLTextAreaElement).value })}
              placeholder="Describe your business, role, or anything else the assistant should know about you."
              style={{ ...textInputStyle, minHeight: 120, resize: 'vertical' }}
              disabled={loading}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
            <button
              onClick={save}
              disabled={!hasChanges || saving || loading}
              style={{
                ...refreshBtnStyle,
                opacity: !hasChanges || saving || loading ? 0.5 : 1,
                cursor: !hasChanges || saving || loading ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            {hasChanges && !saving && (
              <button onClick={() => setDraft(profile)} style={refreshBtnStyle}>
                Cancel
              </button>
            )}
            {savedFlash && <span style={{ fontSize: 12, color: '#81c784' }}>Saved</span>}
          </div>
        </div>

        {/* Locations */}
        <div style={sectionCardStyle}>
          <div style={sectionTitleStyle}>My Locations ({locations.length})</div>
          {loading && locations.length === 0 ? (
            <div style={{ padding: 16, textAlign: 'center', color: 'rgba(250,250,250,0.4)' }}>Loading...</div>
          ) : locations.length === 0 ? (
            <EmptyState label="No saved locations" />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {locations.map((loc) => {
                const isSelected = loc.id === selectedLocationId
                return (
                  <div
                    key={loc.id}
                    onClick={() => setSelectedLocationId(isSelected ? null : loc.id)}
                    style={{
                      ...locationRowStyle,
                      cursor: 'pointer',
                      background: isSelected ? 'rgba(45,165,160,0.15)' : locationRowStyle.background,
                      borderColor: isSelected ? 'rgba(45,165,160,0.4)' : 'rgba(250,250,250,0.08)',
                      display: 'flex',
                      gap: 12,
                    }}
                  >
                    <LocationImage location={loc} size={56} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 500 }}>{loc.title || '(unnamed)'}</div>
                      {loc.address && (
                        <div style={{ fontSize: 13, color: 'rgba(250,250,250,0.65)', marginTop: 2 }}>{loc.address}</div>
                      )}
                      <div style={{ fontSize: 11, color: 'rgba(250,250,250,0.4)', marginTop: 4, display: 'flex', gap: 12 }}>
                        {(loc.latitude != null && loc.longitude != null) && (
                          <span>{loc.latitude.toFixed(5)}, {loc.longitude.toFixed(5)}</span>
                        )}
                        {(loc.updatedAt || loc.updated_at) && <span>Updated {formatDate(loc.updatedAt || loc.updated_at)}</span>}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* Right column — location detail / edit */}
      {selectedLocation && locationDraft && (
        <div style={{ ...detailPanelStyle, flex: 1 }}>
          <div style={{ padding: 16, borderBottom: '1px solid rgba(250,250,250,0.1)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ flex: 1, fontWeight: 600, fontSize: 16 }}>{selectedLocation.title || '(unnamed)'}</span>
            <DeleteButton onClick={() => setConfirmDeleteLocation(true)} />
            <button onClick={() => setSelectedLocationId(null)} style={refreshBtnStyle}>X</button>
          </div>

          <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
              <LocationImage location={selectedLocation} size={240} preferFull onZoom={setZoomImageUrl} />
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle}>Title</label>
              <input
                type="text"
                value={locationDraft.title || ''}
                onInput={(e) => setLocationDraft({ ...locationDraft, title: (e.target as HTMLInputElement).value })}
                style={textInputStyle}
              />
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle}>Address</label>
              <textarea
                value={locationDraft.address || ''}
                onInput={(e) => setLocationDraft({ ...locationDraft, address: (e.target as HTMLTextAreaElement).value })}
                style={{ ...textInputStyle, minHeight: 60, resize: 'vertical' }}
              />
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ ...fieldStyle, flex: 1 }}>
                <label style={labelStyle}>Latitude</label>
                <input
                  type="number"
                  step="0.000001"
                  value={locationDraft.latitude ?? ''}
                  onInput={(e) => {
                    const v = (e.target as HTMLInputElement).value
                    setLocationDraft({ ...locationDraft, latitude: v === '' ? undefined : parseFloat(v) })
                  }}
                  style={textInputStyle}
                />
              </div>
              <div style={{ ...fieldStyle, flex: 1 }}>
                <label style={labelStyle}>Longitude</label>
                <input
                  type="number"
                  step="0.000001"
                  value={locationDraft.longitude ?? ''}
                  onInput={(e) => {
                    const v = (e.target as HTMLInputElement).value
                    setLocationDraft({ ...locationDraft, longitude: v === '' ? undefined : parseFloat(v) })
                  }}
                  style={textInputStyle}
                />
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
              <button
                onClick={saveLocation}
                disabled={!locationHasChanges || savingLocation}
                style={{
                  ...refreshBtnStyle,
                  opacity: !locationHasChanges || savingLocation ? 0.5 : 1,
                  cursor: !locationHasChanges || savingLocation ? 'not-allowed' : 'pointer',
                }}
              >
                {savingLocation ? 'Saving...' : 'Save'}
              </button>
              {locationHasChanges && !savingLocation && (
                <button onClick={() => setLocationDraft({ ...selectedLocation })} style={refreshBtnStyle}>
                  Cancel
                </button>
              )}
              {locationSavedFlash && <span style={{ fontSize: 12, color: '#81c784' }}>Saved</span>}
            </div>

            <div style={{ marginTop: 20, paddingTop: 12, borderTop: '1px solid rgba(250,250,250,0.1)', fontSize: 11, color: 'rgba(250,250,250,0.4)' }}>
              ID: {selectedLocation.id}
              {(selectedLocation.createdAt || selectedLocation.created_at) && (
                <div>Created {formatDate(selectedLocation.createdAt || selectedLocation.created_at)}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {confirmDeleteLocation && selectedLocation && (
        <ConfirmDialog
          message={`Delete location "${selectedLocation.title || '(unnamed)'}"?`}
          onConfirm={deleteLocation}
          onCancel={() => setConfirmDeleteLocation(false)}
        />
      )}

      {zoomImageUrl && (
        <FullscreenImageViewer src={zoomImageUrl} onClose={() => setZoomImageUrl(null)} />
      )}
    </div>
  )
}

const sectionCardStyle: Record<string, string | number> = {
  padding: 20,
  background: 'rgba(0,51,68,0.6)',
  border: '1px solid rgba(250,250,250,0.1)',
  borderRadius: 12,
  marginBottom: 16,
}

const sectionTitleStyle: Record<string, string | number> = {
  fontSize: 13,
  fontWeight: 600,
  color: '#2DA5A0',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  marginBottom: 12,
}

const fieldStyle: Record<string, string | number> = {
  marginBottom: 14,
}

const labelStyle: Record<string, string | number> = {
  display: 'block',
  fontSize: 12,
  fontWeight: 500,
  color: 'rgba(250,250,250,0.6)',
  marginBottom: 6,
}

const textInputStyle: Record<string, string | number> = {
  width: '100%',
  padding: '10px 14px',
  fontSize: 14,
  color: 'var(--text)',
  background: 'var(--bg-surface)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
}

const locationRowStyle: Record<string, string | number> = {
  padding: 12,
  background: 'rgba(0,34,51,0.6)',
  border: '1px solid rgba(250,250,250,0.08)',
  borderRadius: 8,
}

const detailPanelStyle: Record<string, string | number> = {
  background: 'rgba(0,34,51,0.6)',
  borderLeft: '1px solid rgba(250,250,250,0.1)',
  display: 'flex',
  flexDirection: 'column',
  minWidth: 360,
}
